import React from "react";
import { Col } from "react-bootstrap";
import { createActionColumn, createLayoutOptions, } from "../../../assets/configuration/gridConfig";
import { IControlStyles, PageContent, useControlStyles } from "../../../assets/layouts";
import { GRID_ACTION_TYPES, SetState } from "../../../assets/types";
import { Constants } from "../../../assets/utils/lib/constants";
import Control from "../../../assets/vfx/controls/control";
import Form from "../../../assets/vfx/form/form";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { BannersGridDto, SearchBannerDto } from "../../../services/dto";
import { SortDirection, SortModel, } from "../../../assets/utils/lib/searchDto";
import { BannerService } from "./bannerService";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { useParams } from "react-router";
import { createBannerGridColumns } from "./bannerGridColumns";
import { VfxLib } from "../../../assets/vfx";
import { ControlDatagridActionButtonProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { useResolvedBannerCommunityId } from "./bannerCommunity";
import { useCommunity } from "../../../context/communityContext";

interface BannerPageProps {
    controlStyles?: IControlStyles;
    onCustomEvent?: (actionType: string, dataId: string) => void;
    setCommunityId?: SetState<string | undefined>;
    onTotalRecordsChange?: (total: number) => void;
}

const defaultSortModel: SortModel = {
    field: "SortOrder",
    direction: SortDirection.Ascending,
};

const OPEN_URL_ACTION_TYPE = "open-url";

export const BannerPage = (props: BannerPageProps) => {
    const communityContext = useCommunity();
    const defaultSearchQuery: SearchBannerDto = {
        //communityId: undefined,
        bannerTypeId: undefined,
        bannerId: undefined,
        areas: undefined,
        pageSize: 10,
        pageNumber: 1,
        activeOnly: true,
        sortBy: defaultSortModel,
    };

    const [searchQuery, setSearchQuery] = React.useState<SearchBannerDto>(defaultSearchQuery);
    const [formResetKey, setFormResetKey] = React.useState<number>(0);
    const styles: IControlStyles = props.controlStyles ?? useControlStyles();
    const { communityId: paramCommunityId } = useParams();
    const bannerGridQuery = BannerService.gridQuery(communityContext.communityId, searchQuery);
    const bannerLocationQuery = BannerService.getBannerLocationLookups(communityContext.communityId);
    const isGridLoading = bannerGridQuery.isLoading || bannerGridQuery.isFetching;
    const banners: BannersGridDto[] = bannerGridQuery.data?.result ?? [];
    const actionColumn = createActionColumn(styles, "id", [GRID_ACTION_TYPES.EDIT, GRID_ACTION_TYPES.DELETE]);
    const bannerActionButtons = React.useCallback(
        (row: BannersGridDto): ControlDatagridActionButtonProps[] => {
            const actionButtons = VfxLib.datagridActionButtons({ datagridStyles: styles });
            const rowUrl = row.url?.trim();
            const urlButton: ControlDatagridActionButtonProps = {
                image: "bi bi-globe",
                display_type: "image-only",
                style: styles.datagridActionButton,
                image_style: styles.datagridActionButtonIcon,
                eventType: OPEN_URL_ACTION_TYPE,
                title: "Open Url",
            };

            return [
                ...(rowUrl ? [urlButton] : []),
                actionButtons.edit,
                actionButtons.delete,
            ];
        },
        [styles]
    );
    const layoutOptions = React.useMemo(() => createLayoutOptions(true), []);
    const gridColumns = React.useMemo(() => createBannerGridColumns(), []);
    const totalRecords =
        bannerGridQuery.data?.totalSize ??
        (bannerGridQuery.data?.result?.length ?? 0);

    React.useEffect(() => {
        props.onTotalRecordsChange?.(totalRecords);
    }, [totalRecords, props]);

    React.useEffect(() => {
        if (!communityContext.communityId) return;

        setSearchQuery(prev => prev );

        if (!paramCommunityId) {
            props.setCommunityId?.(communityContext.communityId);
        }
    }, [communityContext.communityId, paramCommunityId, props]);

    const handleSearch = (values: SearchBannerDto): void => {
        const next: SearchBannerDto = {
            ...defaultSearchQuery,
            sortBy: searchQuery.sortBy,
            pageSize: searchQuery.pageSize ?? 10,
            pageNumber: 1,

            bannerTypeId:
                !values.bannerTypeId || values.bannerTypeId === Constants.emptyGuid
                    ? undefined
                    : values.bannerTypeId,

            areas:
                !values.areas || values.areas === Constants.emptyGuid
                    ? undefined
                    : values.areas,

            activeOnly: values.activeOnly ?? true,
        };

        setSearchQuery(next);
    };

    function handleNew() {
        props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);
    }

    const handleClear = (): void => {
        setSearchQuery(prev => ({
            ...defaultSearchQuery,
            sortBy: prev.sortBy,
            pageSize: prev.pageSize ?? 10,
            bannerTypeId: undefined,
            areas: undefined,
            activeOnly: true,
        }));
        
        setFormResetKey(prev => prev + 1);
    };

    const handleActiveOnlyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQuery(prev => ({
            ...prev,
            activeOnly: e.target.checked,
            pageNumber: 1,
        }));
    };

    const handleAreaChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const selectedArea = e.target.value;

        setSearchQuery(prev => ({
            ...prev,
            areas:
                !selectedArea || selectedArea === Constants.emptyGuid
                    ? undefined
                    : selectedArea,
            pageNumber: 1,
        }));
    };

    const handleGridCustomEvent = (actionType: string, dataId: string): void => {
        if (actionType === OPEN_URL_ACTION_TYPE) {
            const banner = banners.find((item) => {
                const itemId = (item as BannersGridDto & { id?: string }).id ?? item.iD;
                return itemId === dataId;
            });
            const url = banner?.url?.trim();

            if (!url) return;

            const targetUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
            window.open(targetUrl, "_blank", "noopener,noreferrer");
            return;
        }

        props.onCustomEvent?.(actionType, dataId);
    };

    const renderFilterComponent: React.JSX.Element = (
        <SearchContainer
            key={formResetKey}
            initialValues={{
                bannerTypeId: Constants.emptyString,
                areas: searchQuery.areas ?? Constants.emptyGuid,
                activeOnly: searchQuery.activeOnly ?? true
            }}
            onSearch={handleSearch}
            onClear={handleClear}
            enableReinitialize
        >

            <Col lg={3} className="ms-2">
                <Form.Dropdown
                    name={"areas"}
                    label={"Area"}
                    blankOption={{
                        key: Constants.emptyGuid,
                        display: "--Select Location--",
                        value: Constants.emptyGuid,
                    }}
                    options={LookupDto.mapToOptions(bannerLocationQuery.data)}
                    isLoading={bannerLocationQuery.isLoading}
                    onChange={handleAreaChange}
                />
            </Col>
            <Col lg={2} className="ms-4">
                <Form.Switch
                    name="activeOnly"
                    label="Active"
                    checked={searchQuery.activeOnly ?? true}
                    onChange={handleActiveOnlyChange}
                />
            </Col>
        </SearchContainer>
    );

    return (
        <PageContent hideHeader>
            <Control.DataGrid
                dataId="id"
                cols={gridColumns}
                actionCol={{
                    ...actionColumn,
                    width: "180px",
                    actionButtons: bannerActionButtons,
                }}
                onCustomEvent={handleGridCustomEvent}
                totalRecords={totalRecords}
                layoutOptions={layoutOptions}
                query={searchQuery}
                data={banners}
                loading={isGridLoading}
                onQueryChange={(patch) => setSearchQuery(query => ({ ...query, ...patch }))}
                uiOptions={{
                    title: "Banner",
                    subtitle: "View and manage all banners in your community",
                    filterContent: renderFilterComponent,
                    primaryAction: {
                        text: "Add Banner",
                        icon: "bi bi-plus-lg",
                        onClick: handleNew,
                    },
                }}
            />
        </PageContent>
    );
}